/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { useAtomValue } from 'jotai'

import type { MtaKitStoryblok } from '@/lib/storyblok/types/generated-types'
import { selectedCountryAtom } from '@/store/country'
import { getPricingZoneByCountryCode } from '@/utils/pricing-zones'

/** Represents the calculated price information. */
type KitPriceInfo = {
  amount: number
  currency: 'GBP' | 'USD'
}

/**
 * Custom hook to calculate the price for an MTa Kit based on Storyblok data
 * and the user's selected country.
 *
 * @param kitData - The MtaKitStoryblok data object.
 * @returns The calculated KitPriceInfo object { amount, currency } or null if no pricing is found.
 */
const useKitPrice = (kitData: MtaKitStoryblok): KitPriceInfo | null => {
  const selectedCountry = useAtomValue(selectedCountryAtom)

  if (!kitData) {
    return null
  }

  // Determine the pricing zone based on the selected country
  const currentZone = selectedCountry ? getPricingZoneByCountryCode(selectedCountry.code) : null

  // Try to find matching zone pricing
  if (currentZone && kitData.zone_pricing && kitData.zone_pricing.length > 0) {
    const zonePrice = kitData.zone_pricing.find((p) => p.zone === currentZone)
    if (zonePrice) {
      // Ensure amount is parsed correctly
      const amount = parseFloat(zonePrice.amount)
      if (!isNaN(amount)) {
        return {
          amount,
          currency: zonePrice.currency,
        }
      }
    }
  }

  // Fallback to default pricing if no zone price found or applicable
  if (kitData.default_pricing && kitData.default_pricing.length > 0) {
    const defaultPrice = kitData.default_pricing[0]

    // Ensure amount is parsed correctly
    const amount = parseFloat(defaultPrice.amount)
    if (!isNaN(amount)) {
      return {
        amount,
        currency: defaultPrice.currency,
      }
    }
  }

  // No pricing information found
  return null
}

export { useKitPrice }
export type { KitPriceInfo }
